/**
 * Educational content for SVG to Code page
 */

export const SvgToCodeEducational = () => {
  return (
    <div className="space-y-8 mb-8">
      {/* Introduction Section */}
      <section className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
        <h2 className="text-2xl font-bold text-white mb-4">
          Convert SVG Files to Clean, Optimized Code
        </h2>
        <p className="text-slate-300 mb-4">
          Transform your Scalable Vector Graphics (SVG) files into clean, production-ready code that you can 
          directly embed in your HTML, React components, or CSS. Our SVG to code converter extracts the markup, 
          validates the structure, and provides syntax-highlighted output that's ready to use in your web projects.
        </p>
        <p className="text-slate-300">
          Unlike traditional image formats like PNG or JPG, SVG files are vector-based and scale infinitely without 
          quality loss. By converting SVGs to inline code, you gain better performance, easier customization with CSS 
          and JavaScript, and improved SEO since search engines can read the SVG markup directly.
        </p>
      </section>

      {/* Benefits Section */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">
          Why Convert SVG to Code?
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-slate-800/50 rounded-lg p-5 border border-slate-700">
            <h3 className="text-lg font-medium text-indigo-400 mb-2">🚀 Better Performance</h3>
            <p className="text-slate-300 text-sm">
              Inline SVG code eliminates additional HTTP requests, reducing page load time. Your graphics load instantly 
              as part of the HTML document, improving Core Web Vitals and overall user experience.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-5 border border-slate-700">
            <h3 className="text-lg font-medium text-indigo-400 mb-2">🎨 Full Styling Control</h3>
            <p className="text-slate-300 text-sm">
              Inline SVGs can be styled with CSS just like any other HTML element. Change colors, apply hover effects, 
              add animations, and create dark mode variants without editing the original graphic file.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-5 border border-slate-700">
            <h3 className="text-lg font-medium text-indigo-400 mb-2">⚡ JavaScript Interactivity</h3>
            <p className="text-slate-300 text-sm">
              Access and manipulate SVG elements with JavaScript. Create interactive charts, animated icons, dynamic 
              infographics, and responsive visualizations that update in real-time based on user interactions.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-5 border border-slate-700">
            <h3 className="text-lg font-medium text-indigo-400 mb-2">📱 Responsive Design</h3>
            <p className="text-slate-300 text-sm">
              SVG code scales perfectly on any screen size or resolution, from smartphones to 4K displays. No need for 
              multiple image versions (@2x, @3x) like with raster graphics, saving bandwidth and simplifying maintenance.
            </p>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">
          Common Use Cases for Inline SVG Code
        </h2>
        <div className="space-y-3">
          <div className="bg-slate-800/50 rounded-lg p-4 border-l-4 border-indigo-500">
            <h3 className="font-medium text-white mb-2">Website Icons & Logos</h3>
            <p className="text-slate-300 text-sm">
              Embed company logos, navigation icons, and social media symbols directly in your HTML. This ensures they 
              load instantly and can be styled to match your brand colors dynamically, including support for dark mode themes.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border-l-4 border-indigo-500">
            <h3 className="font-medium text-white mb-2">React & Vue Components</h3>
            <p className="text-slate-300 text-sm">
              Create reusable icon components in modern JavaScript frameworks. Convert your SVG files to JSX/TSX code 
              that can be imported as React components, making your UI library consistent and maintainable.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border-l-4 border-indigo-500">
            <h3 className="font-medium text-white mb-2">Data Visualizations & Charts</h3>
            <p className="text-slate-300 text-sm">
              Build custom charts, graphs, and infographics with full control over every element. Animate data points, 
              add interactive tooltips, and create real-time updating dashboards using JavaScript to manipulate the SVG DOM.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border-l-4 border-indigo-500">
            <h3 className="font-medium text-white mb-2">Animated Graphics & Illustrations</h3>
            <p className="text-slate-300 text-sm">
              Create engaging animations using CSS animations or JavaScript libraries like GSAP. Inline SVG lets you target 
              individual paths, shapes, and groups for complex motion graphics that enhance user engagement.
            </p>
          </div>
        </div>
      </section>

      {/* Best Practices Section */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">
          Best Practices for Using SVG Code
        </h2>
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <ul className="space-y-3 text-slate-300">
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Optimize SVG files before conversion:</strong> Remove unnecessary metadata, 
                comments, and hidden layers from your SVG files using tools like SVGO to reduce code size by 30-50%.
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Use semantic class names:</strong> Add meaningful class names to SVG elements 
                for easier CSS targeting. This makes your code more maintainable and allows for better styling control.
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Include accessibility attributes:</strong> Always add title and desc tags, 
                plus ARIA labels, to make your SVG graphics accessible to screen readers and improve SEO.
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Consider file size for complex graphics:</strong> For highly detailed 
                illustrations with thousands of paths, sometimes using an image format (PNG/WebP) is more efficient than 
                inline SVG code.
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Use CSS custom properties for colors:</strong> Replace hard-coded colors 
                in your SVG with CSS variables (var(--primary-color)) to enable easy theming and dark mode support.
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Cache reusable SVG symbols:</strong> For icons used multiple times, use 
                the SVG &lt;symbol&gt; and &lt;use&gt; elements to define the graphic once and reference it throughout 
                your page, reducing code duplication.
              </div>
            </li>
          </ul>
        </div>
      </section>

      {/* Technical Details Section */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">
          Understanding SVG Code Structure
        </h2>
        <p className="text-slate-300 mb-4">
          SVG files use XML-based syntax to define vector graphics. When you convert an SVG file to code, you get the 
          raw markup that browsers interpret to render the graphic. Here's what the main elements mean:
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
            <code className="text-indigo-400 font-mono">&lt;svg&gt;</code>
            <p className="text-slate-300 text-sm mt-2">
              The root element that defines the SVG canvas. Contains attributes like width, height, viewBox (defines the 
              coordinate system), and xmlns (XML namespace).
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
            <code className="text-indigo-400 font-mono">&lt;path&gt;</code>
            <p className="text-slate-300 text-sm mt-2">
              Defines complex shapes using a series of commands (M for move, L for line, C for curve). The "d" attribute 
              contains the path data that describes the shape.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
            <code className="text-indigo-400 font-mono">&lt;g&gt;</code>
            <p className="text-slate-300 text-sm mt-2">
              Groups multiple elements together. Useful for applying transforms or styles to several shapes at once, 
              and for organizing complex graphics into logical sections.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
            <code className="text-indigo-400 font-mono">fill & stroke</code>
            <p className="text-slate-300 text-sm mt-2">
              Presentation attributes that define colors. "fill" sets the interior color, "stroke" sets the outline color. 
              Both accept color names, hex codes, or RGB/HSL values.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
